import { useState } from "react";
import { ArrowRight, ChevronRight } from "lucide-react";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "../../../../components/ui/collapsible";
import { Badge } from "../../../../components/ui/badge";
import { titleCase, formatSnapshotValue } from "../../../../lib/approvalLabels";
import {
  diffArrayByLabel,
  flattenColumns,
  PROCEDURE_ALLOWED_FIELDS,
  COLUMN_ALLOWED_FIELDS,
} from "../../../../lib/controlDiffUtils";
import type { ArrayDiffItem } from "../../../../lib/controlDiffUtils";

const SECTION_LABEL_KEYS: Record<string, string> = {
  attributes: "reference",
  key_report_items: "name",
  procedures: "name",
  custom_fields: "name",
};

const SECTION_DISPLAY_NAMES: Record<string, string> = {
  procedures: "Workpaper Tables",
};

const SECTION_ALLOWED_KEYS: Record<string, Set<string>> = {
  procedures: PROCEDURE_ALLOWED_FIELDS,
};

interface ArraySectionDiffProps {
  field: string;
  oldItems: Array<Record<string, unknown>>;
  newItems: Array<Record<string, unknown>>;
}

const BORDER_CLASSES: Record<ArrayDiffItem["kind"], string> = {
  added: "border-l-4 border-l-green-400 bg-green-50/50",
  removed: "border-l-4 border-l-red-400 bg-red-50/50",
  modified: "border-l-4 border-l-amber-400 bg-amber-50/50",
};

const KIND_LABELS: Record<ArrayDiffItem["kind"], string> = {
  added: "Added",
  removed: "Removed",
  modified: "Modified",
};

const KIND_BADGE_VARIANTS: Record<
  ArrayDiffItem["kind"],
  "default" | "destructive" | "secondary" | "outline"
> = {
  added: "default",
  removed: "destructive",
  modified: "secondary",
};

function toArray(value: unknown): Array<Record<string, unknown>> {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (item): item is Record<string, unknown> =>
      typeof item === "object" && item !== null,
  );
}

function ColumnSubDiff({
  oldColumns,
  newColumns,
}: {
  oldColumns: Array<Record<string, unknown>>;
  newColumns: Array<Record<string, unknown>>;
}) {
  const flatOld = flattenColumns(oldColumns);
  const flatNew = flattenColumns(newColumns);
  const columnDiffs = diffArrayByLabel(
    flatOld,
    flatNew,
    "name",
    COLUMN_ALLOWED_FIELDS,
  );

  if (columnDiffs.length === 0) return null;

  return (
    <div className="mt-1 space-y-1.5">
      {columnDiffs.map((colDiff, i) => (
        <div
          key={`${colDiff.label}-${colDiff.kind}-${i}`}
          className="text-xs space-y-0.5"
        >
          <div className="flex items-center gap-1.5">
            <Badge
              variant={KIND_BADGE_VARIANTS[colDiff.kind]}
              className="text-[10px] px-1.5 py-0"
            >
              {KIND_LABELS[colDiff.kind]}
            </Badge>
            <span className="font-medium">{colDiff.label}</span>
          </div>
          {colDiff.kind === "modified" &&
            colDiff.changedKeys &&
            colDiff.oldItem &&
            colDiff.changedKeys.map((k) => (
              <div key={k} className="ml-4 text-xs">
                <span className="font-medium text-stone-500">
                  {titleCase(k)}
                </span>
                <div className="mt-0.5 space-y-0.5">
                  <div className="text-stone-400 line-through break-words">
                    {formatSnapshotValue(colDiff.oldItem?.[k])}
                  </div>
                  <div className="font-medium break-words">
                    {formatSnapshotValue(colDiff.item[k])}
                  </div>
                </div>
              </div>
            ))}
        </div>
      ))}
    </div>
  );
}

function ModifiedItemDetails({ diff }: { diff: ArrayDiffItem }) {
  if (!diff.changedKeys || !diff.oldItem) return null;

  return (
    <div className="mt-2 space-y-1">
      {diff.changedKeys.map((k) => {
        if (k === "columns") {
          return (
            <div key={k}>
              <span className="font-medium text-stone-500 text-xs">
                Columns
              </span>
              <ColumnSubDiff
                oldColumns={toArray(diff.oldItem?.[k])}
                newColumns={toArray(diff.item[k])}
              />
            </div>
          );
        }
        return (
          <div key={k} className="text-xs">
            <span className="font-medium text-stone-500">{titleCase(k)}</span>
            <div className="mt-0.5 space-y-0.5">
              <div className="text-stone-400 line-through break-words">
                {formatSnapshotValue(diff.oldItem?.[k])}
              </div>
              <div className="flex items-baseline gap-1.5">
                <ArrowRight className="h-3 w-3 text-stone-400 flex-shrink-0" />
                <span className="font-medium break-words">
                  {formatSnapshotValue(diff.item[k])}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function ArraySectionDiff({
  field,
  oldItems,
  newItems,
}: ArraySectionDiffProps) {
  const [open, setOpen] = useState(true);
  const labelKey = SECTION_LABEL_KEYS[field] ?? "name";
  const allowedKeys = SECTION_ALLOWED_KEYS[field];
  const diffs = diffArrayByLabel(oldItems, newItems, labelKey, allowedKeys);
  const displayName = SECTION_DISPLAY_NAMES[field] ?? titleCase(field);

  if (diffs.length === 0) return null;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex items-center gap-2 w-full text-left py-2 text-sm font-semibold hover:text-stone-700">
        <ChevronRight
          className={`h-4 w-4 text-stone-400 transition-transform ${open ? "rotate-90" : ""}`}
        />
        {displayName}
        <Badge variant="outline" className="text-xs ml-1">
          {diffs.length} {diffs.length === 1 ? "change" : "changes"}
        </Badge>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="space-y-2 pl-6 pb-2">
          {diffs.map((diff, index) => (
            <div
              key={`${diff.label}-${diff.kind}-${index}`}
              className={`rounded-md px-3 py-2 text-sm ${BORDER_CLASSES[diff.kind]}`}
            >
              <div className="flex items-center gap-2">
                <Badge
                  variant={KIND_BADGE_VARIANTS[diff.kind]}
                  className="text-xs"
                >
                  {KIND_LABELS[diff.kind]}
                </Badge>
                <span className="font-medium">{diff.label}</span>
              </div>
              {diff.kind === "modified" && diff.changedKeys && diff.oldItem && (
                <ModifiedItemDetails diff={diff} />
              )}
            </div>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
